import React from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';

const stickyHeaderStyle = {
  backgroundColor: '#1976d2',
  color: '#fff',
  fontWeight: 600,
  fontSize: '0.875rem',
  padding: '12px 8px',
  borderRight: '1px solid rgba(255,255,255,0.2)',
  textAlign: 'center',
  position: 'sticky',
  top: 0,
  zIndex: 2,
};

const SalesForecastCurrentQuarterTable = ({ classes, data, isLoading }) => {
  return (
    <Box mt={4}>
      <Paper className={classes.paper}>
        <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {data?.title || 'Sales Forecast – Current Quarter'}
          </Typography>
          <span className={classes.updateFrequency}>
            {data?.updateFrequency || 'Weekly update - Every Friday PM'}
          </span>
        </Box>

        {isLoading ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Skeleton variant="rect" animation="wave" height={200} width="100%" />
            </Grid>
            <Grid item xs={12}>
              <Skeleton variant="rect" animation="wave" height={300} width="100%" />
            </Grid>
          </Grid>
        ) : (
          <>
            {/* Summary + Won Deals Section Header */}
            <Box mb={2}>
              <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#4472c4' }}>
                Summary + Won Deals – Week {data?.currentWeekNumber || 'X'}
              </Typography>
            </Box>

            {/* Summary Table */}
            <Box mb={3}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                Summary
              </Typography>
              <TableContainer>
                <Table size="small" style={{ maxWidth: 400 }}>
                  <TableHead>
                    <TableRow className={classes.tableHeader}>
                      <TableCell style={{ textAlign: 'center' }}></TableCell>
                      <TableCell style={{ textAlign: 'center' }}># of Opps</TableCell>
                      <TableCell style={{ textAlign: 'center' }}>ACV ($M)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell align="center"><strong>Won QTD</strong></TableCell>
                      <TableCell align="center">{data?.summary?.won?.count || 0}</TableCell>
                      <TableCell align="center">${data?.summary?.won?.acvInMillions || '0.00'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="center"><strong>Forecast</strong></TableCell>
                      <TableCell align="center">{data?.summary?.forecast?.count || 0}</TableCell>
                      <TableCell align="center">${data?.summary?.forecast?.acvInMillions || '0.00'}</TableCell>
                    </TableRow>
                    <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell align="center"><strong>Total</strong></TableCell>
                      <TableCell align="center"><strong>{data?.summary?.total?.count || 0}</strong></TableCell>
                      <TableCell align="center"><strong>${data?.summary?.total?.acvInMillions || '0.00'}</strong></TableCell>
                    </TableRow>
                    <TableRow style={{ backgroundColor: '#fff9c4' }}>
                      <TableCell align="center"><strong>Quota</strong></TableCell>
                      <TableCell align="center">-</TableCell>
                      <TableCell align="center"><strong>${data?.summary?.quotaInMillions || '0.00'}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Won Opportunities Table */}
            <Box mb={3}>
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
                Won Opportunities
              </Typography>
              <TableContainer className={classes.tableContainer} style={{ maxHeight: '80vh', overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell style={stickyHeaderStyle}>#</TableCell>
                      <TableCell style={stickyHeaderStyle}>Opportunity</TableCell>
                      <TableCell style={stickyHeaderStyle}>Owner</TableCell>
                      <TableCell style={stickyHeaderStyle}>ACV ($K)</TableCell>
                      <TableCell style={stickyHeaderStyle}>Disc. %</TableCell>
                      <TableCell style={stickyHeaderStyle}>Won Date</TableCell>
                      <TableCell style={stickyHeaderStyle}>Age since<br/>Qual. (Days)</TableCell>
                      <TableCell style={{ ...stickyHeaderStyle, borderRight: 'none' }}>Partner</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.wonOpps?.map((opp, index) => (
                      <TableRow
                        key={opp.oppId || index}
                        className={opp.isNewThisWeek ? classes.newOpportunityRow : classes.tableRow}
                      >
                        <TableCell className={classes.tableCell} align="center">{opp.rowNum}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.opportunity}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.owner}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{opp.acvInK}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{/* TODO: Discount % */}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{opp.wonDate}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{opp.ageSinceQualDays}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.partner}</TableCell>
                      </TableRow>
                    ))}
                    {(!data?.wonOpps || data.wonOpps.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" style={{ fontStyle: 'italic', color: '#666' }}>
                          No won opportunities this quarter
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Forecast Opportunities Table */}
            <Box>
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
                Forecast Opportunities (Most Likely + Commit)
              </Typography>
              <TableContainer className={classes.tableContainer} style={{ maxHeight: '80vh', overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell style={stickyHeaderStyle}>#</TableCell>
                      <TableCell style={stickyHeaderStyle}>Opportunity</TableCell>
                      <TableCell style={stickyHeaderStyle}>Owner</TableCell>
                      <TableCell style={stickyHeaderStyle}>ACV ($K)</TableCell>
                      <TableCell style={stickyHeaderStyle}>Disc. %</TableCell>
                      <TableCell style={stickyHeaderStyle}>Projected<br/>Close Date</TableCell>
                      <TableCell style={stickyHeaderStyle}>Age since<br/>Qual. (Days)</TableCell>
                      <TableCell style={stickyHeaderStyle}>Stage</TableCell>
                      <TableCell style={stickyHeaderStyle}>Days in<br/>Stage</TableCell>
                      <TableCell style={stickyHeaderStyle}>Partner</TableCell>
                      <TableCell style={stickyHeaderStyle}>Forecast<br/>Category</TableCell>
                      <TableCell style={{ ...stickyHeaderStyle, borderRight: 'none' }}>Next Steps</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.forecastOpps?.map((opp, index) => (
                      <TableRow
                        key={opp.oppId || index}
                        className={opp.isNewThisWeek ? classes.newOpportunityRow : classes.tableRow}
                      >
                        <TableCell className={classes.tableCell} align="left">{opp.rowNum}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.opportunity}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.owner}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{opp.acvInK}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{/* TODO: Discount % */}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{opp.projectedCloseDate}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{opp.ageSinceQualDays}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.stageChar}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{/* TODO: Days in Stage */}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.partner}</TableCell>
                        <TableCell className={classes.tableCell} align="left">{opp.forecastCategory}</TableCell>
                        <TableCell className={classes.tableCell} align="left" style={{ maxWidth: 200, fontSize: '0.75rem' }}>{/* TODO: Next Steps */}</TableCell>
                      </TableRow>
                    ))}
                    {(!data?.forecastOpps || data.forecastOpps.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={12} align="center" style={{ fontStyle: 'italic', color: '#666' }}>
                          No forecast opportunities for this quarter
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Legend for green highlighting */}
            <Box mt={2}>
              <div className={classes.legend}>
                <div className={classes.legendItem}>
                  <div className={classes.legendBox} style={{ backgroundColor: '#c8e6c9' }} />
                  <Typography variant="body2">New this week</Typography>
                </div>
              </div>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default SalesForecastCurrentQuarterTable;
